<template lang="pug">
    label(
        class="_check",
        :class="_class",
        :style="{ width, height }",
        v-on="__on")
        input(
            ref="_in",
            type="checkbox",
            :id="id",
            :name="name",
            :value="val",
            :required="required",
            :disabled="disabled",
            :inert="inert",
            :tabindex="inert ? -1 : tabindex",
            v-on="__on",
            @change="changed")
        span(
            class="_cbc",
            :class="{ ['_'+tone]: tone, _fvis: this.fvis }",
            aria-hidden="true") &zwnj;
        span(
            v-if="$slots.default",
            class="_cbl")
            slot
</template>

<script>
    import entry from './ib-form-entry.vue';

    /**
     * A stylized checkbox control that supports all v-model behavior of the raw input control.
     * The `label` click event is overridden because the default JS behavior will fire the click
     * event twice if the `label` area surrounding a checkbox or radio button is clicked.
     */
    export default {
        name: 'ib-checkbox',

        mixins: [
            entry
        ],

        props: {
            /**
             * If boolean, checkbox is checked if true. If array, checkbox is checked
             * if the array contains `val`.
             */
            value: {
                type: [
                    Boolean,  // checked = set to true
                    Array     // checked = contains this.val
                ],
                default: undefined
            },
            /**
             * Value to watch for if `v-model` is array.
             */
            val: [String,Number],
            /**
             * Appearance modifier: `frameless` is supported on the ios platform.
             */
            appearance: String,
            /**
             * Take focus when made visible
             */
            autofocus: Boolean,
            /**
             * Semantic color class.
             */
            tone: String
        },

        data() {
            return {
                fvis: false
            };
        },

        computed: {
            _class() {
                return {
                    _fo: this.focused,
                    _inv: this.showvalid && !this.valid,
                    ['_e'+this.appearance]: this.appearance
                }
            }
        },

        watch: {
            val() {
                this._sync();
            }
        },

        methods: {
            /** Callback for check/uncheck */
            changed() {
                this.validate();
                this.input(this.$refs._in.checked);
            },

            /** Can be called externally to simulate a click */
            click() {
                this.input(!this.$refs._in.checked);
                this.$refs._in.focus();
            },

            /** Retrieve desired check state based on value */
            checked(value) {
                if (value instanceof Array) {
                    return value.includes(this.val);
                } else {
                    return value;
                }
            },

            /** @private */
            _focus(e) {
                this.fvis = e.timeStamp < __app.tabts;
            },

            /** @private */
            _blur(e) {
                this.fvis = false;
            },

            /** @private */
            _click(e) {
                this.click();
                e.preventDefault();
            },

            /** @private */
            _sync() {
                const input = this.$refs._in;
                const checked = this.checked(this.ivalue);
                if (input.checked != checked) {
                    input.checked = checked;
                    this.validate();
                }
            },

            input(checked) {
                if (this.ivalue instanceof Array) {
                    if (checked) {
                        this._set(this.ivalue.concat(this.val));
                    } else {
                        this._set(this.ivalue.filter(val => val != this.val));
                    }
                } else {
                    this._set(checked);
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._check,
    ._radio,
    ._toggle {
        display: inline-flex;
        align-items: baseline;
        cursor: pointer;
        white-space: nowrap;
        -webkit-tap-highlight-color: transparent;

        /** Invisible input element; proxied to allow styling on IE */
        > input {
            position: absolute;
            margin: 0;
            padding: 0;
            width: 0;
            height: 0;
            opacity: 0;
            border: none;
                -ms-appearance: none;
               -moz-appearance: none;
            -webkit-appearance: none;
                    appearance: none;
            &[disabled] + ._cbc,
            &[disabled] + ._cbc + ._cbt {
                opacity: .4;
            }
        }

        &._inv > ._cbc {
            color: @fg-error;
        }
    }

    ._cbc {
        flex: 0 0 auto;
        display: inline-block;
        position: relative;
    }

    ._cbl {
        margin: 0 8px;
        white-space: initial;
    }

    ._check {
        > ._cbc {
            width: @checkbox-size;
            height: @checkbox-size;
            line-height: @checkbox-lh;
            color: @fg-accent;
            .checkbox-default();
        }
        &._eframeless > ._cbc {
            .checkbox-frameless();
        }
        > input:hover + ._cbc {
            .checkbox-hover();
        }
        &._fo > input + ._cbc {
            .checkbox-focus();
        }
        &._inv > input + ._cbc {
            .checkbox-invalid();
        }
        > input:checked + ._cbc {
            .checkbox-checked();
        }
        &._eframeless > input:checked + ._cbc {
            .checkbox-frameless-checked();
        }
    }
</style>

<ib-example id="tone" name="Tone">
    <template lang="pug">
        ib-form
            .after-8 Food groups: 
                span(id="value") {{value}}
            .before-4
                ib-checkbox(name="group", v-model="value", val="milk") The Milk Group
            .before-4
                ib-checkbox(name="group", v-model="value", val="meat", tone="error") The Meat Group
            .before-4
                ib-checkbox(name="group", v-model="value", val="fruit", tone="positive") The Fruit Group
            .before-4
                ib-checkbox(name="group", v-model="value", val="bread", tone="buy") The Bread Group
            .before-4
                ib-checkbox(name="group", v-model="value", val="beer", tone="important") The Beer Group
    </template>
    <script>
        {
            data() {
                return {
                    value: []
                };
            }
        }
    </script>
</ib-example>

<ib-example id="attrs" name="Attributes">
    <template lang="pug">
        ib-form
            .after-8 Value: 
                span(id="value") {{value}}
            .before-4
                ib-checkbox(name="attrs", v-model="value", val="disabled", disabled) disabled
            .before-4
                ib-checkbox(name="attrs", v-model="value", val="readonly", readonly) readonly
            .before-4
                ib-checkbox(name="attrs", v-model="value", val="inert", inert) inert
    </template>
    <script>
        {
            data() {
                return {
                    value: []
                };
            }
        }
    </script>
</ib-example>
